/**
 * Mixer feedback poller — OPBridge `GET /mixer/status` → live strip state.
 *
 * The Ableton → iPad half of the mixer (DEC-467). The OP-AbletonMixer M4L device
 * pushes Live's per-track volume/mute/name/meter to OPBridge (device → udpsend
 * 11202 → OPBridge `MixerState`); this poller reads that snapshot and drives the
 * `applyMixer*` appliers in mixer/session.ts so a fader moved in Ableton updates
 * the iPad strip. Mirrors clockPoll.ts (primary-only per DEC-063, poll not push,
 * silent on 401/503/network). The control half (iPad → Ableton) is unchanged —
 * it rides `/osc/send`.
 *
 * Contract gotchas (from OPApps/opbridge-opbuttons.md DEC-467, verified live):
 *   - `stale: true` is NORMAL (device sends on-change only) — render the latest
 *     snapshot regardless; stale/age_us drive a "synced Xs ago" affordance only.
 *   - `tracks.length` may exceed `count` (never-truncate) — apply the first count.
 *   - `index` is a 0-based bank position == the absolute track index the session
 *     keys by; apply straight through (tolerate remap on a rescan).
 *   - `has_ever_received: false` = no device ever — don't apply; surface an empty
 *     state via `mixerFeedback`.
 *   - volume is echo-suppressed per strip (a dragged fader ignores its own echo
 *     for ~250ms); mute/name/meter are applied straight.
 *
 * Not built here (follow-up): the optional `mixer_update` event on /ws/consumer
 * (30 Hz batched meters) for lower-latency meter streaming. The poll covers
 * volume/mute/name reliably + meters at poll rate.
 */

import { createSignal, type Accessor } from 'solid-js';
import { bridges, getConnection } from './../opbridge/store.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { OpbridgeError, type MixerStatus, type MixerTrackStatus } from '../opbridge/httpClient.ts';
import { watchJob } from '../diag/watchdog.ts';
import {
  applyMixerVolume, applyMixerMute, applyMixerName, applyMixerMeter,
  applyMixerSolo, applyMixerArm, applyMixerPan, applyMixerSend, applyMixerSendCount,
  stripState, volumeEchoSuppressed, panEchoSuppressed, sendEchoSuppressed, MASTER_IDX, RETURN_BASE,
} from './session.ts';

const ACTIVE_POLL_INTERVAL_MS = 250; // 4 Hz while a device is feeding
const IDLE_POLL_INTERVAL_MS = 1_000; // 1 Hz when no device / not ready

/** Live feedback status for the UI (a "synced Xs ago" / "no mixer device"
 *  affordance). `hasEverReceived === false` → show an empty state; `stale` is
 *  NORMAL between changes, not an error. */
export interface MixerFeedbackInfo {
  /** A primary is connected AND /mixer/status answered this session. */
  connected: boolean;
  /** The device has connected at least once (else render "no mixer device"). */
  hasEverReceived: boolean;
  /** No update in ~5s — normal between changes; use for the "synced Xs ago" note. */
  stale: boolean;
  /** Tracks the device reports (render this many strips). */
  count: number;
  /** ms since the last device update (from age_us). */
  ageMs: number;
}

const EMPTY: MixerFeedbackInfo = { connected: false, hasEverReceived: false, stale: true, count: 0, ageMs: 0 };
const [feedbackSig, setFeedbackSig] = createSignal<MixerFeedbackInfo>(EMPTY);

/** Reactive feedback status. Read by the Mixer page for a device-present /
 *  synced affordance. */
export const mixerFeedback: Accessor<MixerFeedbackInfo> = feedbackSig;

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;
let inFlight = false;
let lastGeneration = -1;   // skip re-applying an unchanged snapshot

/** Start polling. Idempotent — a second call doesn't stack timers. */
export function startMixerStatusPoll(): void {
  if (timer !== null) return;
  stopped = false;
  schedule(IDLE_POLL_INTERVAL_MS);
}

/** Stop + tear down. Used by App onCleanup + tests. */
export function stopMixerStatusPoll(): void {
  stopped = true;
  if (timer !== null) { clearTimeout(timer); timer = null; }
  setFeedbackSig(EMPTY);
  lastGeneration = -1;
}

/** Force an immediate poll (foreground/resume), like pokeClockPoll. */
export function pokeMixerStatusPoll(): void {
  if (stopped || inFlight) return;
  if (timer !== null) { clearTimeout(timer); timer = null; }
  void tick();
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => { timer = null; void tick(); }, delayMs);
}

async function tick(): Promise<void> {
  if (stopped || inFlight) return;
  inFlight = true;
  try { await tickInner(); } finally { inFlight = false; }
}

async function tickInner(): Promise<void> {
  const cfg = pickPrimaryConnected();
  const conn = cfg ? getConnection(cfg.id) : null;
  if (!cfg || !conn) {
    setFeedbackSig(EMPTY);
    schedule(IDLE_POLL_INTERVAL_MS);
    return;
  }
  watchJob('mixer-poll');

  let active = false;
  try {
    const status = await conn.httpClient().mixerStatus();
    active = applySnapshot(status);
  } catch (err) {
    // 401 (bearer lacks /mixer/status — shouldn't happen with opbuttons-full v5),
    // 503 (mixer not wired), network abort — all expected; skip silently. Keep
    // the last-known strips (don't blank on a transient miss). Only a hard
    // "no primary" (above) resets to EMPTY.
    void err;
  }

  schedule(active ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS);
}

/** Apply one /mixer/status snapshot to the strip session. Returns true when a
 *  device is actively present (drives the faster cadence). */
function applySnapshot(s: MixerStatus): boolean {
  const ageMs = Math.max(0, Math.round((s.age_us ?? 0) / 1000));
  setFeedbackSig({
    connected: true,
    hasEverReceived: !!s.has_ever_received,
    stale: !!s.stale,
    count: s.count | 0,
    ageMs,
  });

  // No device has ever connected → nothing to render; leave strips at their
  // optimistic defaults and let the UI show a "no mixer device" empty state.
  if (!s.has_ever_received) { lastGeneration = -1; return false; }

  // Unchanged snapshot → nothing to apply (cheap poll continues for freshness).
  // Never skip while a strip is mid-drag echo-suppressed: a value we skipped
  // last tick still needs to land once the finger lifts, and the generation
  // may not bump again. `anySuppressed` forces the per-field diff pass.
  const anySuppressed = (s.tracks ?? []).some((t) => volumeEchoSuppressed(t.index) || panEchoSuppressed(t.index));
  if (s.generation === lastGeneration && !anySuppressed) return true;
  lastGeneration = s.generation;

  // Apply the first `count` tracks (tracks.length may exceed count). Per-field
  // diff against current state so an unchanged field doesn't churn re-renders.
  const n = Math.min(s.count | 0, (s.tracks ?? []).length);
  for (let i = 0; i < n; i += 1) {
    applyOneTrack(s.tracks[i]!, s.tracks[i]!.index | 0);
  }

  // Master track (device v0.2 `/master_*`) — keyed on the reserved index -1, same
  // echo-suppression contract as a regular strip's volume/pan.
  if (s.master) {
    const m = s.master;
    const cur = stripState(MASTER_IDX);
    if (m.volume !== undefined) {
      const vol = clamp01(m.volume);
      if (vol !== cur.volume && !volumeEchoSuppressed(MASTER_IDX)) applyMixerVolume(MASTER_IDX, vol);
    }
    if (m.pan !== undefined) {
      const pan = clampBipolar(m.pan);
      if (pan !== cur.pan && !panEchoSuppressed(MASTER_IDX)) applyMixerPan(MASTER_IDX, pan);
    }
    if (m.meter !== undefined) {
      const meter = clamp01(m.meter);
      if (meter !== cur.meter) applyMixerMeter(MASTER_IDX, meter);
    }
  }

  // Return tracks (device v0.3 `/return_*`) — same per-strip apply as regular
  // tracks, keyed on RETURN_BASE + index (returns never carry `armed`).
  if (Array.isArray(s.returns)) {
    for (let i = 0; i < s.returns.length; i += 1) {
      const r = s.returns[i]!;
      applyOneTrack(r, RETURN_BASE + (r.index | 0));
    }
  }
  return true;
}

/** Apply one track-shaped status to a session key (regular track index, or
 *  RETURN_BASE + return index). Per-field diff + echo-suppression, keyed. */
function applyOneTrack(t: MixerTrackStatus, key: number): void {
  const cur = stripState(key);
  const cleanName = typeof t.name === 'string' ? t.name.replace(/^"(.*)"$/, '$1') : '';
  if (cleanName !== cur.name) applyMixerName(key, t.name);
  if (!!t.muted !== cur.mute) applyMixerMute(key, !!t.muted);
  const meter = clamp01(t.meter);
  if (meter !== cur.meter) applyMixerMeter(key, meter);
  const vol = clamp01(t.volume);
  if (vol !== cur.volume && !volumeEchoSuppressed(key)) applyMixerVolume(key, vol);
  // Fields present only once the device/OPBridge send them (absent = no-op).
  if (t.solo !== undefined && !!t.solo !== cur.solo) applyMixerSolo(key, !!t.solo);
  if (t.armed !== undefined && !!t.armed !== cur.arm) applyMixerArm(key, !!t.armed);
  if (t.pan !== undefined) {
    const pan = clampBipolar(t.pan);
    if (pan !== cur.pan && !panEchoSuppressed(key)) applyMixerPan(key, pan);
  }
  if (Array.isArray(t.sends)) {
    for (let sIdx = 0; sIdx < t.sends.length; sIdx += 1) {
      const sv = clamp01(t.sends[sIdx]!);
      if (sv !== (cur.sends[sIdx] ?? 0) && !sendEchoSuppressed(key, sIdx)) applyMixerSend(key, sIdx, sv);
    }
  }
  if (t.sendCount !== undefined && t.sendCount !== cur.sendCount) applyMixerSendCount(key, t.sendCount);
}

function pickPrimaryConnected(): BridgeConfig | null {
  // DEC-063: mixer feed comes from the primary only.
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  if (conn && conn.status() === 'connected') return primary;
  return null;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function clampBipolar(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(-1, Math.min(1, n));
}

// ─── DEV test hook (drive the apply path without a live daemon) ──────────────
declare global {
  interface Window {
    __opbuttonsMixer?: {
      /** Feed a fake /mixer/status snapshot straight into the production apply
       *  path (for smokes without a live OP-AbletonMixer device). */
      injectStatus(s: MixerStatus): void;
      feedback(): MixerFeedbackInfo;
    };
  }
}
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsMixer = {
    injectStatus: (s) => { applySnapshot(s); },
    feedback: () => feedbackSig(),
  };
}
